import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/ui/sonner';
import MGMTStackLogo from '../components/shared/MGMTStackLogo';

const MGMTLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate('/mgmt-app');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0a0612]">
      {/* Animated Purple Haze Background */}
      <div className="absolute inset-0">
        {/* Gradient orbs */}
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-30 blur-3xl animate-pulse"
          style={{ background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)' }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-25 blur-3xl animate-pulse"
          style={{ background: 'radial-gradient(circle, #EC4899 0%, transparent 70%)', animationDelay: '1s' }}
        />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, #A855F7 0%, transparent 60%)' }}
        />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center p-4 w-full max-w-lg">
        {/* Login Card with Logo Inside */}
        <Card className="w-full bg-[#1a1025]/80 border-purple-500/20 backdrop-blur-xl shadow-2xl shadow-purple-500/10">
          <CardContent className="p-10">
            {/* Logo Inside Card - Divider centered in card */}
            <div className="flex justify-center items-center mb-8">
              {/* Logo on left - takes equal space */}
              <div className="flex-1 flex justify-end pr-5">
                <img 
                  src="/mgmt-logo.png" 
                  alt="MGMT Stack" 
                  className="w-[180px] h-[180px] object-contain"
                />
              </div>
              
              {/* Centered Divider */}
              <div 
                className="w-px h-20 bg-white/60 flex-shrink-0"
                style={{ boxShadow: '0 0 8px 2px rgba(255, 255, 255, 0.4)' }}
              />
              
              {/* Text on right - takes equal space */}
              <div className="flex-1 flex justify-start pl-5">
                <div className="flex flex-col leading-none">
                  <span className="text-3xl font-bold bg-gradient-to-r from-[#8B5CF6] via-[#A855F7] to-[#EC4899] bg-clip-text text-transparent">
                    MGMT
                  </span>
                  <span className="text-3xl font-bold text-white/90 -mt-0.5">
                    Stack
                  </span>
                </div>
              </div>
            </div>
            
            {/* Divider */}
            <div className="relative mb-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-purple-500/20"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#1a1025] px-3 text-purple-300/50">Sign in to continue</span>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="mgmt-login-form">
              <div>
                <Label className="text-purple-200/80 text-sm">Email</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="mgmt-email-input"
                  className="mt-2 h-12 bg-[#0d0815]/60 border-purple-500/30 text-white placeholder:text-purple-300/40 focus:border-purple-400 focus:ring-purple-400/20"
                />
              </div>
              <div>
                <Label className="text-purple-200/80 text-sm">Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="mgmt-password-input"
                  className="mt-2 h-12 bg-[#0d0815]/60 border-purple-500/30 text-white placeholder:text-purple-300/40 focus:border-purple-400 focus:ring-purple-400/20"
                />
              </div>
              
              <Button
                type="submit"
                data-testid="mgmt-login-submit-btn"
                className="w-full h-12 bg-gradient-to-r from-[#8B5CF6] via-[#A855F7] to-[#EC4899] hover:opacity-90 text-white font-semibold shadow-lg shadow-purple-500/25 transition-all hover:shadow-purple-500/40"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : 'Sign In'}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-purple-300/60">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="text-purple-400 font-semibold hover:text-purple-300 transition-colors"
              >
                Sign up
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="mt-8 text-purple-400/40 text-xs">
          © 2025 Mgmt Stack. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default MGMTLogin;
